/**
 * Billing / shipping address rules: what each field lets through as it is
 * typed (filterAddressInput), what a complete address must be before it is
 * saved or paid with (validateAddress), and the form it is stored in
 * (normalizeAddress). The API checks the same rules
 * (aldoo-na/store/address-validation.js) — keep the two in step.
 *
 * validateAddress returns { field: code } for the fields that fail; the site
 * words each code (ADDRESS_ERROR_CODES) in its own language.
 *
 * What reaches the API is not always what a form typed: a field may come as
 * a number, an array or an object, 10 000 characters long, or with a country
 * that is no country. Each of those fails its field's rule rather than being
 * stored or turned into a database error.
 */

// letters of any script, digits, and the punctuation an address uses
const TEXT_CHARS = /[^\p{L}\p{M}\d\s.,'’"„“\-\/№#()]/gu
const NAME_CHARS = /[^\p{L}\p{M}\s.'’\-]/gu
const CITY_CHARS = /[^\p{L}\p{M}\d\s.'’\-()]/gu
const PHONE_CHARS = /[^\d+\s\-()]/g

// postal codes by country; the countries not here take 2–10 letters/digits
export const POSTAL_CODES = {
  // a Bulgarian code never starts with 0 (1000 is Sofia, 9999 the highest)
  BG: /^[1-9]\d{3}$/,
  AT: /^\d{4}$/,
  BE: /^\d{4}$/,
  HR: /^\d{5}$/,
  CY: /^\d{4}$/,
  CZ: /^\d{3} ?\d{2}$/,
  DK: /^\d{4}$/,
  EE: /^\d{5}$/,
  FI: /^\d{5}$/,
  FR: /^\d{5}$/,
  DE: /^\d{5}$/,
  GR: /^\d{3} ?\d{2}$/,
  HU: /^\d{4}$/,
  IE: /^[A-Z\d]{3} ?[A-Z\d]{4}$/i,
  IT: /^\d{5}$/,
  LV: /^(LV-?)?\d{4}$/i,
  LT: /^(LT-?)?\d{5}$/i,
  LU: /^(L-?)?\d{4}$/i,
  MT: /^[A-Z]{3} ?\d{2,4}$/i,
  NL: /^\d{4} ?[A-Z]{2}$/i,
  PL: /^\d{2}-?\d{3}$/,
  PT: /^\d{4}-?\d{3}$/,
  RO: /^\d{6}$/,
  SK: /^\d{3} ?\d{2}$/,
  SI: /^\d{4}$/,
  ES: /^\d{5}$/,
  SE: /^\d{3} ?\d{2}$/,
  GB: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
  US: /^\d{5}(-\d{4})?$/,
}
const POSTAL_ANY = /^[A-Z\d][A-Z\d \-]{1,9}$/i

// the countries the shop delivers to: the ones the address form offers, each
// with its postal code rule above
export const SUPPORTED_COUNTRIES = Object.keys(POSTAL_CODES)

// the longest each field may be once its spaces are collapsed; the form cuts
// what is typed at the same length (filterAddressInput)
export const MAX_LENGTH = { name: 80, line1: 120, line2: 120, city: 60, state: 60, postal_code: 10, country: 2, phone: 20, email: 254, company: 120 }

export const ADDRESS_FIELDS = ["name", "line1", "line2", "city", "state", "postal_code", "country", "phone"]

// the countries whose postal codes are only digits (numeric keypad, digits only)
const DIGIT_POSTAL = new Set(["BG", "AT", "BE", "HR", "CY", "DK", "EE", "FI", "FR", "DE", "HU", "IT", "RO", "SI", "ES", "US", "CZ", "GR", "SK", "SE", "PL", "PT"])
const POSTAL_LENGTH = { BG: 4, AT: 4, BE: 4, CY: 4, DK: 4, HU: 4, SI: 4, HR: 5, EE: 5, FI: 5, FR: 5, DE: 5, IT: 5, ES: 5, RO: 6, CZ: 6, GR: 6, SK: 6, SE: 6, PL: 6, PT: 8, US: 10 }

export const ADDRESS_ERROR_CODES = [
  "name_required",
  "name_invalid",
  "line1_required",
  "line1_invalid",
  "line1_number",
  "line2_invalid",
  "city_required",
  "city_invalid",
  "state_invalid",
  "postal_code_required",
  "postal_code_invalid",
  "country_required",
  "country_invalid",
  "phone_required",
  "phone_invalid",
]

export const ADDRESS_ERROR_MESSAGES = {
  name_required: "Please enter the recipient name",
  name_invalid: "Enter a real name: first and last name, letters only",
  line1_required: "Please enter the street address",
  line1_invalid: "Enter a real street address: street, number, block",
  line1_number: "Add the street number or block",
  line2_invalid: "This line has characters an address does not use",
  city_required: "Please enter the city",
  city_invalid: "Enter a real city or village name",
  state_invalid: "Enter a real region name",
  postal_code_required: "Please enter the postal code",
  postal_code_invalid: "This is not a postal code of the selected country",
  country_required: "Please select a country",
  country_invalid: "Select a country from the list",
  phone_required: "Please enter a phone number",
  phone_invalid: "Enter a real phone number, e.g. +359 88 123 4567",
}

const letters = (value) => (String(value).match(/\p{L}/gu) || []).length
export const collapse = (value) => String(value ?? "").replace(/\s+/g, " ").trim()
// "aaaa", "!!!!", "1111111": a key held down, not an address
const repeated = (value) => /(.)\1{3,}/u.test(String(value).replace(/\d/g, ""))
// at least one vowel-bearing word: keyboard mashing ("qwrtpsdfg") has none
const hasWord = (value) => /[aeiouyаъоуеиюяіїєё]/iu.test(value) || !/\p{Script=Latin}|\p{Script=Cyrillic}/u.test(value)
// a field is text or nothing: a number, an array or an object is not an answer to it
export const isText = (value) => value === undefined || value === null || typeof value === "string"
export const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)

/**
 * A person's name: letters (any script), spaces, dots, apostrophes and
 * hyphens, at least three letters, no key held down, nothing a keyboard mash
 * makes. `full` asks for first and last name (a parcel's recipient); without
 * it one word is a name (what a profile calls someone). Returns the error
 * code, or null.
 */
export function personNameError(value, { full = true, required = true } = {}) {
  if (!isText(value)) return "name_invalid"
  const name = collapse(value)
  if (!name) return required ? "name_required" : null
  if (name.length > MAX_LENGTH.name || name !== name.replace(NAME_CHARS, "") || letters(name) < 3 || repeated(name) || !hasWord(name)) return "name_invalid"
  if (full && name.split(" ").filter((part) => letters(part) >= 1).length < 2) return "name_invalid"
  return null
}

/**
 * The phone as a number can be dialled: international (+359881234567) when
 * it has a country code or is Bulgarian, the digits as written for another
 * country's national number; null when it cannot be a phone number.
 */
export function normalizePhone(phone, country = "BG") {
  const raw = collapse(phone)
  if (!raw || raw.slice(1).includes("+")) return null
  const digits = raw.replace(/\D/g, "")
  if (/^(\d)\1+$/.test(digits.slice(-7))) return null
  let international = null
  if (raw.startsWith("+")) international = digits
  else if (digits.startsWith("00")) international = digits.slice(2)
  else if (country === "BG" && digits.startsWith("0")) international = "359" + digits.slice(1)
  else if (country === "BG" && digits.startsWith("359")) international = digits
  if (!international) return digits.length >= 7 && digits.length <= 15 ? digits : null
  if (international.length < 8 || international.length > 15) return null
  // Bulgaria: 8–9 digits after 359; a mobile (87/88/89/98/99…) has 9
  if (international.startsWith("359")) {
    const national = international.slice(3)
    if (!/^[2-9]\d{7,8}$/.test(national)) return null
    if (/^(8[7-9]|9[89])/.test(national) && national.length !== 9) return null
  }
  return `+${international}`
}

/** What a field keeps of what was typed into it. */
export function filterAddressInput(field, value, country = "BG") {
  const text = String(value ?? "")
  if (field === "phone") return text.replace(PHONE_CHARS, "").replace(/(?!^)\+/g, "").slice(0, 20)
  if (field === "name") return text.replace(NAME_CHARS, "").slice(0, 80)
  if (field === "city" || field === "state") return text.replace(CITY_CHARS, "").slice(0, 60)
  if (field === "postal_code") {
    if (DIGIT_POSTAL.has(country)) return text.replace(/[^\d\s\-]/g, "").slice(0, POSTAL_LENGTH[country] || 10)
    return text.replace(/[^A-Za-z\d\s\-]/g, "").toUpperCase().slice(0, 10)
  }
  if (field === "line1" || field === "line2") return text.replace(TEXT_CHARS, "").slice(0, 120)
  return text
}

/** The keyboard a field asks a phone for. */
export const addressInputMode = (field, country = "BG") => {
  if (field === "phone") return "tel"
  if (field === "postal_code" && DIGIT_POSTAL.has(country)) return "numeric"
  return "text"
}

/**
 * { field: code } for every field that fails; empty when the address is good.
 * `only` limits the answer to some fields: an office or locker order asks
 * only who collects the parcel (name, phone), a company invoice everything
 * but those two.
 */
export function validateAddress(address = {}, { only = null } = {}) {
  const a = isRecord(address) ? address : {}
  const errors = {}
  // what is not text fails as it is; the rest is read with its spaces collapsed
  const value = {}
  for (const field of ADDRESS_FIELDS) {
    value[field] = isText(a[field]) ? collapse(a[field]) : ""
    if (!isText(a[field]) || value[field].length > MAX_LENGTH[field]) errors[field] = `${field}_invalid`
  }
  const { name, line1, line2, city, state, phone } = value
  const postal = value.postal_code
  const country = value.country.toUpperCase()

  if (!errors.name) {
    const code = personNameError(name)
    if (code) errors.name = code
  }

  if (errors.line1) {
    // not text, or too long
  } else if (!line1) errors.line1 = "line1_required"
  else if (line1 !== line1.replace(TEXT_CHARS, "") || letters(line1) < 3 || line1.length < 5 || repeated(line1) || !hasWord(line1)) errors.line1 = "line1_invalid"
  else if (!/\d/.test(line1 + " " + line2)) errors.line1 = "line1_number"

  if (!errors.line2 && line2 && (line2 !== line2.replace(TEXT_CHARS, "") || repeated(line2))) errors.line2 = "line2_invalid"

  if (errors.city) {
    // not text, or too long
  } else if (!city) errors.city = "city_required"
  else if (city !== city.replace(CITY_CHARS, "") || letters(city) < 2 || repeated(city) || !hasWord(city)) errors.city = "city_invalid"

  if (!errors.state && state && (state !== state.replace(CITY_CHARS, "") || letters(state) < 2 || repeated(state))) errors.state = "state_invalid"

  if (errors.country) {
    // not text
  } else if (!country) errors.country = "country_required"
  else if (!SUPPORTED_COUNTRIES.includes(country)) errors.country = "country_invalid"

  if (errors.postal_code) {
    // not text, or too long
  } else if (!postal) errors.postal_code = "postal_code_required"
  else if (!(POSTAL_CODES[country] || POSTAL_ANY).test(postal)) errors.postal_code = "postal_code_invalid"

  if (errors.phone) {
    // not text, or too long
  } else if (!phone) errors.phone = "phone_required"
  else if (phone !== phone.replace(PHONE_CHARS, "") || !normalizePhone(phone, SUPPORTED_COUNTRIES.includes(country) ? country : "BG")) errors.phone = "phone_invalid"

  if (!only) return errors
  return Object.fromEntries(Object.entries(errors).filter(([field]) => only.includes(field)))
}

/**
 * The address as it is stored: every field trimmed and its spaces collapsed,
 * the country and a postal code with letters in capitals, the phone in its
 * international form (+359881234567). `fields` keeps only some of them — an
 * office order keeps who collects it and nothing the buyer typed besides.
 * Only a valid address is normalized; what fails stays the caller's to refuse.
 */
export function normalizeAddress(address = {}, fields = ADDRESS_FIELDS) {
  const a = isRecord(address) ? address : {}
  const country = collapse(a.country).toUpperCase()
  const out = {}
  for (const field of fields) {
    const text = isText(a[field]) ? collapse(a[field]) : ""
    if (field === "country") out.country = country
    else if (field === "postal_code") out.postal_code = text.toUpperCase()
    else if (field === "phone") out.phone = (text && normalizePhone(text, SUPPORTED_COUNTRIES.includes(country) ? country : "BG")) || text
    else out[field] = text
  }
  return out
}

export const isValidAddress = (address) => Object.keys(validateAddress(address)).length === 0
